import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '~/hooks/auth';
import { formatPhoneNumber } from '~/utils/formatePhoneNumber';
import QRCode from 'react-qr-code';

const {
  REACT_APP_FIREBASE_PROJECT,
  REACT_APP_CHATBOT_WPP,
  REACT_APP_CHATBOT_WPP_SEND_MESSAGE_ONLY,
} = process.env;

let interval: any;
let countInterval: any;
let statusInterval: any;

const chatbotHost = REACT_APP_CHATBOT_WPP;

interface WhatsAppContact {
  id: {
    server: string;
    user: string;
    _serialized: string;
  };
  number: string;
  isBusiness: boolean;
  isEnterprise: boolean;
  pushname: string | null;
  type: 'in' | 'out';
  isMe: boolean;
  isUser: boolean;
  isGroup: boolean;
  isWAContact: boolean;
  isMyContact: boolean;
  isBlocked: boolean;
  paused: boolean;
}

let wppInterval: any;

export const ChatbotWhatsAppSetup: React.FC = () => {
  const { company } = useAuth();

  const [status, setStatus] = useState(0);
  const [qrCode, setQrCode] = useState('');
  const [counter, setCounter] = useState(60);
  const [open, setOpen] = useState(false);
  const [paused, setPaused] = useState(false);
  const [search, setSearch] = useState('');

  const [contacts, setContacts] = useState<WhatsAppContact[]>([]);

  const statusType = useMemo(() => {
    if (status === -1) return 'icon-status-danger';
    if (status === 0) return 'icon-status-warning';
    if (status === 1) return 'icon-status-info';

    return 'icon-status-danger';
  }, [status]);

  const stopTryConnection = useCallback(() => {
    console.log('STOP TRY CONNECTION');
    clearInterval(countInterval);
    clearInterval(interval);
    clearInterval(statusInterval);
    setStatus(-1);
  }, []);

  const contactsFiltered = useMemo(() => {
    if (!search) {
      return contacts;
    }

    return contacts.filter(item => item.number.includes(search));
  }, [search, contacts]);

  useEffect(() => {
    async function getContacts() {
      const response = await axios.get(
        `${chatbotHost}/session/${REACT_APP_FIREBASE_PROJECT}/contacts`,
      );

      setContacts(response.data.contacts);
    }

    if (status === 1 && !REACT_APP_CHATBOT_WPP_SEND_MESSAGE_ONLY) {
      getContacts();
    }
  }, [status]);

  // verificar status a cada 7 segundos
  useEffect(() => {
    async function statusChatbot() {
      try {
        const response = await axios.get(
          `${chatbotHost}/session/${REACT_APP_FIREBASE_PROJECT}/status`,
        );

        if (response.data.ready) {
          clearInterval(countInterval);
          clearInterval(interval);
          clearInterval(statusInterval);
          setStatus(1);
          setQrCode('');
        }

        if (response.data.paused) {
          setPaused(true);
        }
      } catch (err) {
        stopTryConnection();
      }
    }

    if (!statusInterval) {
      statusInterval = setInterval(() => {
        statusChatbot();
      }, 7000);
    }
  }, [stopTryConnection]);

  // atualizar o qrcode a cada 60 segundos
  useEffect(() => {
    async function showQrCode() {
      try {
        const response = await axios.get(
          `${chatbotHost}/session/${REACT_APP_FIREBASE_PROJECT}/qrcode`,
        );

        if (response.data.qr) {
          clearInterval(interval);
          setQrCode(response.data.qr);
          setCounter(60);

          countInterval = setInterval(() => {
            setCounter(value => value - 1);
          }, 1000);
        }
      } catch (err) {
        stopTryConnection();
      }
    }

    async function reloadQrCode() {
      if (counter === 0) {
        clearInterval(countInterval);
        showQrCode();
      }
    }

    reloadQrCode();
  }, [counter, stopTryConnection]);

  /**
   * Iniciar o chatbot
   * Verificar se o chatbot está online
   * Exibe o qrcode
   */
  useEffect(() => {
    async function showQrCode() {
      try {
        const response = await axios.get(
          `${chatbotHost}/session/${REACT_APP_FIREBASE_PROJECT}/qrcode`,
        );

        if (response.data.qr) {
          clearInterval(interval);
          setQrCode(response.data.qr);

          countInterval = setInterval(() => {
            setCounter(value => value - 1);
          }, 1000);
        }
      } catch (err) {
        stopTryConnection();
      }
    }

    async function startChatbot() {
      try {
        await axios.post(
          REACT_APP_CHATBOT_WPP_SEND_MESSAGE_ONLY
            ? `${chatbotHost}/session/${REACT_APP_FIREBASE_PROJECT}/disabled`
            : `${chatbotHost}/session/${REACT_APP_FIREBASE_PROJECT}`,
        );

        await showQrCode();

        interval = setInterval(async () => {
          await showQrCode();
        }, 5000);
      } catch (err) {
        stopTryConnection();
      }
    }

    async function statusChatbot() {
      try {
        console.log('obtendo status...');
        const response = await axios.get(
          `${chatbotHost}/session/${REACT_APP_FIREBASE_PROJECT}/status`,
        );

        if (!response.data.ready) {
          console.log('OFFLINE');
          setStatus(0);
          await startChatbot();
        }

        if (response.data.paused) {
          console.log('PAUSED');
          clearInterval(countInterval);
          clearInterval(interval);
          clearInterval(statusInterval);
          setStatus(1);
          setPaused(true);
        }

        if (response.data.ready) {
          console.log('PRONTO');
          clearInterval(countInterval);
          clearInterval(interval);
          clearInterval(statusInterval);
          setStatus(1);
        }
      } catch (err) {
        stopTryConnection();
      }
    }

    if (company.uid) statusChatbot();
  }, [company, stopTryConnection]);

  const handlePauseContact = useCallback(
    async (id: string, paused: boolean) => {
      try {
        const response = await axios.put(
          `${chatbotHost}/session/${REACT_APP_FIREBASE_PROJECT}/contact/${id}/toggle`,
        );

        setContacts(response.data);
      } catch (err) {
        stopTryConnection();
      }
    },
    [stopTryConnection],
  );

  const handlePauseChatbot = useCallback(async () => {
    try {
      const response = await axios.get(
        `${chatbotHost}/session/${REACT_APP_FIREBASE_PROJECT}/toggle`,
      );

      if (response.data.message === 'paused') {
        setPaused(true);
      } else {
        setPaused(false);
      }
    } catch (err) {
      stopTryConnection();
    }
  }, [stopTryConnection]);

  const handleChangeSearch = useCallback((value: string) => {
    if (wppInterval) clearInterval(wppInterval);

    wppInterval = setInterval(() => {
      setSearch(value);
    }, 1000);
  }, []);

  return (
    <>
      <Link to="#whatsapp-chatbot" className="nk-quick-nav-icon" onClick={()=> setOpen(!open)}>
        <div className={`icon-status ${statusType}`}>
          <em className="icon ni ni-whatsapp" />
        </div>
      </Link>
      {open && (
        <>
          <div className="dropdown-menu dropdown-menu-xl dropdown-menu-right" style={{display: 'block'}}>
            <div className="dropdown-head">
              <span className="sub-title nk-dropdown-title">
                {REACT_APP_CHATBOT_WPP_SEND_MESSAGE_ONLY
                  ? 'WhatsApp'
                  : 'WhatsApp Chatbot'}
              </span>
              {status === 1 && !REACT_APP_CHATBOT_WPP_SEND_MESSAGE_ONLY && (
                <button 
                  className='d-flex justify-content-center align-items-center'
                  style={{ height: '20px', backgroundColor: paused ? 'red' : 'green', color: 'white' }}
                  onClick={() => handlePauseChatbot()}
                >
                  {!paused ? 'Ativo' : 'Pausado'}
                </button>
              )}
            </div>
            <div className="dropdown-body">

              {status === -1 && (
                <div className="p-3 d-flex flex-column justify-content-center align-items-center">
                  <div>WhatsApp está offline.</div>
                </div>
              )}

              {status === 0 && (
                <div className="p-3 d-flex flex-column justify-content-center align-items-center">
                  <div>WhatsApp está tentando se conectar...</div>
                </div>
              )}

              {status === 1 && REACT_APP_CHATBOT_WPP_SEND_MESSAGE_ONLY && (
                <>
                  <div className="px-3 py-1 d-flex flex-column justify-content-center align-items-center dropdown-head">
                    <div className="fw-bold">WhatsApp está online.</div>
                  </div>
                </>
              )}

              {status === 1 && !REACT_APP_CHATBOT_WPP_SEND_MESSAGE_ONLY && (
                <>
                  <div className="px-3 py-1 d-flex flex-column justify-content-center align-items-center dropdown-head">
                    <div className="fw-bold mb-2">WhatsApp está online.</div>
                    <div className='d-flex'>
                      <input
                        placeholder="Pesquisar contato..."
                        onChange={e => handleChangeSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className='px-3 py-1' style={{ maxHeight: 500, overflow: 'auto' }}>
                    {contactsFiltered.map((contact) => (
                      <div key={contact.id._serialized} className='d-flex justify-content-between align-items-center mb-1'>
                        <p className='m-0'>
                          <b>{contact.pushname}</b> - {formatPhoneNumber(contact.id.user)}
                        </p>
                        <button 
                          className='d-flex justify-content-center align-items-center'
                          style={{ height: '20px', backgroundColor: contact.paused ? 'red' : 'green', color: 'white' }}
                          onClick={() => handlePauseContact(contact.id._serialized, !contact.paused)}
                        >
                          {!contact.paused ? 'Ativo' : 'Pausado'}
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {qrCode && (
                <div className='d-flex flex-column justify-content-center align-items-center'>
                  {/* <img src={qrCode} /> */}
                  <QRCode value={qrCode} size={250} />
                  <div className="mt-4">{counter} segundos</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};
